<script setup>
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useRoute } from "vue-router";
import router from "@/router";
import AuthLayout from "@/views/layouts/AuthLayout.vue";

const authStore = useAuthStore();
const route = useRoute();

const regularExpression = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,20}$/;
const email = route.params.email;
const code = route.params.code;

let password = ref(null);
let confirmation_password = ref(null);
let isError = ref(false);
let isPasswordValidation = ref(true);
let isSubmitted = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const checkPasswordValidation = () => {
  isError.value = false;
  isPasswordValidation.value = true;
  if (password.value && confirmation_password.value) {
    if (password.value != confirmation_password.value) {
      isError.value = true;
    } else if (!isError.value) {
      isPasswordValidation.value = regularExpression.test(password.value);
    }
  }
};

const onSubmit = () => {
  isError.value = false;
  isSubmitted.value = true;
  authStore
    .resetPassword({ email, code, password: password.value })
    .then(async () => { await router.push("/"); })
    .catch(() => { isSubmitted.value = false; });
};
</script>

<template>
  <AuthLayout>
    <div class="auth-card">
      <div class="auth-logo-wrap">
        <img class="auth-logo-img" src="@/assets/images/logo/hiddenox.svg" alt="Ravo" />
        <span class="auth-logo-text">Ravo</span>
      </div>

      <p class="welcome-message">RESET PASSWORD</p>
      <p class="second-welcome-message">Enter your new password below</p>

      <!-- New Password -->
      <div class="auth-field-wrap auth-field-wrap--password">
        <span class="p-float-label auth-float">
          <PInput
            id="rp-password"
            :model-value="password"
            @input="password = $event.target.value; checkPasswordValidation()"
            :type="showPassword ? 'text' : 'password'"
            :class="{ 'p-invalid': isError }"
            class="auth-pinput"
            minlength="8"
            maxlength="20"
            autocomplete="new-password"
          />
          <label for="rp-password">New Password</label>
        </span>
        <button type="button" class="auth-eye-btn" @click="showPassword = !showPassword" tabindex="-1">
          <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>

      <!-- Confirm Password -->
      <div class="auth-field-wrap auth-field-wrap--password">
        <span class="p-float-label auth-float">
          <PInput
            id="rp-confirm"
            :model-value="confirmation_password"
            @input="confirmation_password = $event.target.value; checkPasswordValidation()"
            :type="showConfirmPassword ? 'text' : 'password'"
            :class="{ 'p-invalid': isError }"
            class="auth-pinput"
            minlength="8"
            maxlength="20"
            autocomplete="new-password"
          />
          <label for="rp-confirm">Re-enter Password</label>
        </span>
        <button type="button" class="auth-eye-btn" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
          <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>

      <!-- Validation hint -->
      <div style="margin-top: -16px; margin-bottom: 20px; padding: 0 2px;">
        <small v-if="isError" class="auth-field-error">Passwords do not match.</small>
        <small v-else class="auth-hint" :class="{ 'auth-field-error': !isPasswordValidation }">
          8–20 characters, including a number and a special character (!@#$%^&*).
        </small>
      </div>

      <button
        @click="onSubmit"
        :disabled="!password || !confirmation_password || isSubmitted || isError || !isPasswordValidation"
        type="button"
        class="auth-submit-btn"
      >
        RESET PASSWORD
        <span v-if="isSubmitted" class="spinner-sm" role="status" aria-hidden="true"></span>
      </button>

      <p class="forth-welcome-message" style="margin-top: 20px;">Want to login instead?</p>
      <router-link class="auth-link" to="/login" style="display:block; text-align:center; margin-top:8px;">
        Back to Login
      </router-link>
    </div>
  </AuthLayout>
</template>
